import React, { Suspense, useEffect, useState } from "react";
import { Outlet, useLocation } from "lib/router";
import { useDispatch } from "react-redux";
import { CircleLoading } from "components/Loaders";
import { ErrorToaster } from "components/Toaster";
import { addNavigation, setPermission } from "redux/slices/navigationDataSlice";
import AuthServices from "services/Auth";
import { addChildRoutes, getPermissionsRoutes } from "utils";
import N8nChatBot from "components/n8nChatBot";
import { FEATURES } from "config/features";
import { EmptyState, cn } from "ui";
import { Lock } from "lucide-react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import "animate.css";

const COLLAPSE_KEY = "qayd.sidebar.collapsed";

function DashboardLayout() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  // SaaS mode: pages of the other business type (e.g. vehicle payments for a shipping company)
  const [blockedRoutes, setBlockedRoutes] = useState([]);

  // *For Get Side Navigation
  const getSideNavigation = async () => {
    try {
      const { data } = await AuthServices.getSideNavigation();
      dispatch(addNavigation(addChildRoutes(data?.permissions)));
      dispatch(setPermission(getPermissionsRoutes(data?.permissions)));
      setBlockedRoutes(Array.isArray(data?.blocked_routes) ? data.blocked_routes : []);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
      } catch {}
      return !c;
    });

  useEffect(() => {
    getSideNavigation();
  }, []);

  useEffect(() => setMobileOpen(false), [pathname]);

  return (
    <div className="flex h-screen bg-bg">
      {/* Mobile backdrop */}
      {mobileOpen && <div className="fixed inset-0 z-[1190] bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} />}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-[1200] w-[264px] shrink-0 bg-sidebar transition-[width,transform] duration-200 md:static md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          collapsed ? "md:w-[64px]" : "md:w-[264px]",
        )}
      >
        <Sidebar collapsed={collapsed && !mobileOpen} onToggle={mobileOpen ? () => setMobileOpen(false) : toggleCollapsed} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <Header onMenu={() => setMobileOpen(true)} />
        <main className="relative min-h-0 flex-1 overflow-y-auto">
          <Suspense fallback={<CircleLoading />}>
            {blockedRoutes.includes(pathname) ? (
              <div className="p-6">
                <EmptyState
                  icon={Lock}
                  title="Not available for your company"
                  description="This page belongs to a business type your company doesn't use. Ask your Qayd administrator to add it."
                />
              </div>
            ) : (
              <Outlet />
            )}
          </Suspense>
        </main>
      </div>
      {FEATURES.chatbot && <N8nChatBot />}
    </div>
  );
}

export default DashboardLayout;
